<template lang="pug">
.swap-mapping-page
  .page-header.mb-4
    h3.page-title
      Icon(icon="tabler:arrows-exchange")
      | Swap Mapping
    div.d-flex(style="gap: 0.5rem")
      b-button(variant="outline-dark" @click="$refs.tour.start()" title="Show me how this page works")
        Icon(icon="tabler:help-circle")
        | How to
      b-button(variant="primary" @click="openEditor(null)" data-tour="new-mapping")
        Icon(icon="tabler:plus")
        | New Mapping

  how-to-tour(ref="tour" :steps="tourSteps" storage-key="howto_admin_mapping_v1" auto-start)

  //- p.text-muted.
  //-   Mix and match: for each original session, choose which sessions caregivers may pick
  //-   as replacements when swapping. Sessions without a mapping cannot be swapped out of.

  //- Staff-configurable swap rules (read by the caregiver app on every swap)
  .rules-card(data-tour="rules")
    .rules-title
      Icon(icon="tabler:adjustments")
      | Swap rules
    .rules-grid
      .rule-item
        label Max pending swaps per client
        b-form-input(type="number" min="1" size="sm" v-model.number="rules.maxPendingSwaps")
        //- small.text-muted How many upcoming swaps one client may hold at a time.
      .rule-item
        label Session capacity (seats)
        b-form-input(type="number" min="1" size="sm" v-model.number="rules.sessionCapacity")
        //- small.text-muted Swap slots open when seats are free — members on approved MC or swapped out free their seat that day.
      .rule-item
        label Swap window (days after the missed session)
        b-form-input(type="number" min="1" size="sm" v-model.number="rules.swapWindowDays")
        small.text-muted A replacement must fall within this many days of the session being missed. Set a different window for one client under Client overrides.
      //- .rule-item
      //-   label Minimum lead before original session (days)
      //-   b-form-input(type="number" min="0" size="sm" v-model.number="rules.minSwapNoticeDays")
      //-   small.text-muted 1 = tomorrow onward. Replacement dates always have to fall after the coming weekend (next week onward) — that rule is fixed.
    .text-right
      b-button(size="sm" variant="primary" @click="saveRules" :disabled="savingRules")
        b-spinner(small v-if="savingRules")
        span(v-else) Save Rules

  .text-center.py-5(v-if="loading")
    b-spinner(variant="primary")
    p.mt-3.text-muted Loading swap mappings...

  div(v-else)
    //- Existing mappings — table: who goes where, split by gender when the
    //- targets differ (Male-only / Female-only replacement sessions).
    .mapping-legend(data-tour="legend")
      span.legend-item
        span.gender-pill.male M
        | Male clients only (set per target in Edit)
      span.legend-item
        span.gender-pill.female F
        | Female clients only
      span.legend-item
        span.gender-pill.all All
        | Anyone
      //- span.legend-note Gender routing is defined here only — the schedule's own gender flag is ignored by the caregiver app. Staff can still pick any session in New Request.
    table.mapping-table(v-if="mappings.length" data-tour="mapping-table")
      thead
        tr
          th Original session
          th Who
          th Can swap into
          th.text-right Actions
      tbody
        template(v-for="m in mappings")
          tr(v-for="(route, ri) in routesFor(m)" :key="m.id + '-' + ri" :class="{ 'row-first': ri === 0 }")
            td.col-origin(v-if="ri === 0" :rowspan="routesFor(m).length")
              strong {{ scheduleName(m.referenceSchedulerId) }}
              .origin-meta {{ scheduleMeta(m.referenceSchedulerId) }}
            td.col-who
              span.who-pill(:class="route.who") {{ route.label }}
            td.col-targets
              template(v-if="route.targets.length")
                span.target-chip(v-for="e in route.targets" :key="e.id")
                  | {{ scheduleName(e.id) }}
                  small.chip-meta  {{ scheduleMeta(e.id) }}
                  span.gender-pill.inline(:class="effectiveGender(e)") {{ effectiveGenderShort(e) }}
              span.text-muted.small(v-else) No session available for {{ route.label.toLowerCase() }}
            td.col-actions.text-right(v-if="ri === 0" :rowspan="routesFor(m).length")
              b-button(size="sm" variant="outline-primary" @click="openEditor(m)")
                Icon(icon="tabler:pencil")
                | Edit
              b-button.ml-1(size="sm" variant="outline-danger" @click="deleteMapping(m)")
                Icon(icon="tabler:trash")

    //- //- Schedules with no mapping yet
    //- .unmapped-box(v-if="unmappedSchedules.length")
    //-   strong
    //-     Icon(icon="tabler:alert-circle")
    //-     |  {{ unmappedSchedules.length }} session(s) have no swap mapping
    //-   p.small.text-muted.mb-2 Caregivers cannot swap out of these sessions until a mapping is added.
    //-   .target-chips
    //-     span.target-chip.unmapped(v-for="s in unmappedSchedules" :key="s.id" @click="openEditor(null, s.id)" title="Click to create a mapping")
    //-       | {{ labelOf(s) }}

    .text-center.py-5(v-if="!mappings.length && !unmappedSchedules.length")
      p.text-muted No active session schedules found.

    //- Per-client overrides: a client with an entry here uses THIS list instead
    //- of their schedule's mapping (handy for one-off arrangements).
    .client-box(data-tour="client-overrides")
      .d-flex.justify-content-between.align-items-center.mb-2
        .rules-title.mb-0
          Icon(icon="tabler:user-cog")
          | Client overrides
        b-button(size="sm" variant="outline-primary" @click="openClientEditor(null)")
          Icon(icon="tabler:plus")
          | Add client override
      p.small.text-muted.mb-2 A client listed here can get their own list of swap-in sessions (replacing the schedule mapping above, whichever schedule they swap out of) and/or their own swap window, e.g. 90 days for a client who travels.
      p.small.text-muted(v-if="!clientMappingList.length") No client overrides yet.
      .mapping-card(v-for="c in clientMappingList" :key="c.clientId")
        .mapping-main
          .mapping-origin
            .origin-label Client
            strong {{ c.name || clientLabel(c.clientId) }}
            .window-pill(v-if="c.swapWindowDays" :title="'Replacement may be up to ' + c.swapWindowDays + ' days after the missed session'")
              Icon(icon="tabler:calendar-stats")
              | {{ c.swapWindowDays }}-day window
          Icon.mapping-arrow(icon="tabler:arrow-right")
          .mapping-targets
            .origin-label Allowed replacements ({{ c.schedulers.length || 'schedule mapping' }})
            .target-chips
              span.target-chip(v-for="sid in c.schedulers" :key="sid") {{ scheduleLabel(sid) }}
              span.text-muted.small(v-if="!c.schedulers.length") Uses the schedule mapping — only the swap window is overridden
        .mapping-actions
          b-button(size="sm" variant="outline-primary" @click="openClientEditor(c)")
            Icon(icon="tabler:pencil")
            | Edit
          b-button(size="sm" variant="outline-danger" @click="removeClientMapping(c)")
            Icon(icon="tabler:trash")

  //- Client override editor
  b-modal#client-mapping-editor(
    :title="clientEditing.existing ? 'Edit Client Override' : 'New Client Override'"
    centered
    no-close-on-backdrop
    hide-footer
  )
    .form-section
      label.section-label Client
      template(v-if="clientEditing.existing")
        strong {{ clientEditing.name || clientLabel(clientEditing.clientId) }}
      template(v-else)
        b-form-input(size="sm" v-model="clientSearch" placeholder="Search client name..." class="mb-2")
        b-form-select(v-model="clientEditing.clientId" :options="clientOptions" size="sm")
        small.text-muted(v-if="!clients.length") Loading clients...

    .form-section
      label.section-label Swap window for this client (days after the missed session)
      b-form-input(type="number" min="1" size="sm" v-model="clientEditing.swapWindowDays" :placeholder="'Default (' + rules.swapWindowDays + ' days)'" style="max-width: 220px")
      small.text-muted Leave blank to keep the global rule. E.g. 90 lets a client who is away in December make up as late as March.

    .form-section
      label.section-label Sessions this client may swap into
      small.d-block.text-muted.mb-2 Leave all unticked to keep the schedule mapping and only change the swap window.
      .day-group(v-for="group in allScheduleGroups" :key="group.day")
        .day-group-title {{ group.day }}
        b-form-checkbox(
          v-for="s in group.items"
          :key="s.id"
          v-model="clientEditing.schedulers"
          :value="s.id"
        ) {{ labelOf(s) }}
      small.text-muted {{ clientEditing.schedulers.length }} selected

    .d-flex.justify-content-end.mt-3(style="gap: 0.5rem")
      b-button(variant="outline-secondary" @click="$bvModal.hide('client-mapping-editor')" :disabled="savingClient") Cancel
      b-button(variant="primary" @click="saveClientMapping" :disabled="savingClient || !clientEditing.clientId || !clientOverrideValid")
        b-spinner(small v-if="savingClient")
        span(v-else) Save Override

  //- Editor modal
  b-modal#swap-mapping-editor(
    :title="editing.id ? 'Edit Mapping' : 'New Mapping'"
    centered
    no-close-on-backdrop
    hide-footer
  )
    .form-section
      label.section-label Original session
      b-form-select(
        v-model="editing.referenceSchedulerId"
        :options="originOptions"
        :disabled="!!editing.id"
      )
      small.text-muted(v-if="editing.id") The original session can't be changed — delete and re-create the mapping instead.

    .form-section
      label.section-label Replacement sessions caregivers may pick
      .day-group(v-for="group in replacementGroups" :key="group.day")
        .day-group-title {{ group.day }}
        .target-row(v-for="s in group.items" :key="s.id")
          b-form-checkbox(v-model="editing.selected" :value="s.id") {{ labelOf(s) }}
          b-form-select.who-select(
            v-if="editing.selected.includes(s.id)"
            size="sm"
            :value="editing.genders[s.id] == null ? 'any' : String(editing.genders[s.id])"
            :options="whoOptions"
            @change="setEdgeGender(s.id, $event)"
          )
      small.text-muted {{ editing.selected.length }} selected · "Who" limits that target to male or female clients for this original session only

    .d-flex.justify-content-end.mt-3(style="gap: 0.5rem")
      b-button(variant="outline-secondary" @click="$bvModal.hide('swap-mapping-editor')" :disabled="saving") Cancel
      b-button(variant="primary" @click="saveMapping" :disabled="saving || !editing.referenceSchedulerId || !editing.selected.length")
        b-spinner(small v-if="saving")
        span(v-else) Save Mapping
</template>

<script>
import { Icon } from '@iconify/vue2';
import axios from 'axios';
import authModule from '@/store/authenticator';
import HowToTour from '@/components/HowToTour.vue';

export default {
  name: 'MakeupSchedulersPage',
  components: { Icon, HowToTour },
  data() {
    return {
      loading: true,
      // Guided walkthrough (HowToTour). Shown once per browser, replay via "How to".
      tourSteps: [
        { title: 'Swap Mapping', icon: 'tabler:arrows-exchange', text: 'This page decides which sessions a caregiver may swap <b>into</b> when they skip one, and the rules the app applies. Changes reach the caregiver app within a minute. Click <b>How to</b> any time to replay this guide.' },
        { title: '1 · Swap rules', icon: 'tabler:adjustments', target: 'rules', text: '<b>Max pending swaps</b>: how many upcoming swaps one client may hold (default 3). <b>Session capacity</b>: seats per session — a replacement date is only offered while seats are free. <b>Swap window</b>: how many days after the missed session a replacement may fall (default 30). Press <b>Save Rules</b> after editing.' },
        { title: '2 · Read the mapping table', icon: 'tabler:table', target: 'mapping-table', text: 'Each row is an <b>original session</b> and the sessions its members may swap into. When a target is limited to one gender, the row splits into a Male line and a Female line.' },
        { title: 'Who may go where', icon: 'tabler:gender-bigender', target: 'legend', text: 'The <b>M / F / All</b> pills come from the mapping only. The caregiver app ignores the schedule\'s own gender flag — set the restriction per target when editing a mapping.' },
        { title: '3 · Edit or add a mapping', icon: 'tabler:edit', target: 'new-mapping', text: 'Use <b>Edit</b> on a row, or <b>New Mapping</b> for a session that has none. Tick the replacement sessions and choose <b>Who</b> (Anyone, Male only, Female only) for each. A session with no mapping cannot be swapped out of.' },
        { title: '4 · Client overrides', icon: 'tabler:user-cog', target: 'client-overrides', text: 'For a one-off arrangement, add a client here with their own list of allowed sessions (replaces the schedule mapping for that client only, ignoring gender limits) and/or their own <b>swap window</b> — e.g. 90 days for a client travelling in December. Leave the sessions unticked to change only the window.' },
      ],
      saving: false,
      savingRules: false,
      rules: { maxPendingSwaps: 3, sessionCapacity: 28, minSwapNoticeDays: 1, swapWindowDays: 30 },
      mappings: [],
      schedules: [],
      editing: { id: null, referenceSchedulerId: null, selected: [], genders: {} },
      whoOptions: [
        { value: 'any', text: 'Anyone' },
        { value: '0', text: 'Male clients only' },
        { value: '1', text: 'Female clients only' },
      ],
      // Per-client overrides ({ clientId: { name, schedulers[], swapWindowDays? } }) + client list for the picker
      clientMappings: {},
      clients: [],
      clientSearch: '',
      savingClient: false,
      clientEditing: { clientId: null, name: '', schedulers: [], swapWindowDays: '', existing: false }
    };
  },
  computed: {
    scheduleById() {
      return Object.fromEntries(this.schedules.map(s => [s.id, s]));
    },

    unmappedSchedules() {
      const mapped = new Set(this.mappings.map(m => m.referenceSchedulerId));
      return this.schedules.filter(s => !mapped.has(s.id));
    },

    // New mapping: only schedules without one. Editing: locked to its own.
    originOptions() {
      const base = [{ value: null, text: 'Select the original session...' }];
      if (this.editing.id) {
        const s = this.scheduleById[this.editing.referenceSchedulerId];
        return [...base, { value: this.editing.referenceSchedulerId, text: s ? this.labelOf(s) : this.editing.referenceSchedulerId }];
      }
      return [...base, ...this.unmappedSchedules.map(s => ({ value: s.id, text: this.labelOf(s) }))];
    },

    clientMappingList() {
      return Object.entries(this.clientMappings)
        .map(([clientId, v]) => ({ clientId, name: v.name || '', schedulers: v.schedulers || [], swapWindowDays: v.swapWindowDays || null }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },

    // An override needs at least one of: its own targets, or a valid window (whole days ≥ 1).
    clientOverrideValid() {
      const raw = this.clientEditing.swapWindowDays;
      const hasWindow = raw !== '' && raw !== null && raw !== undefined;
      const days = Number(raw);
      if (hasWindow && (!Number.isInteger(days) || days < 1)) return false;
      return this.clientEditing.schedulers.length > 0 || hasWindow;
    },

    clientOptions() {
      const q = this.clientSearch.trim().toLowerCase();
      const taken = new Set(Object.keys(this.clientMappings));
      const list = this.clients
        .filter(c => !taken.has(c.id.toLowerCase()))
        .filter(c => !q || `${c.no} ${c.name}`.toLowerCase().includes(q))
        .slice(0, 200);
      return [{ value: null, text: 'Select a client...' }, ...list.map(c => ({ value: c.id, text: c.no || c.name }))];
    },

    // Every active schedule grouped by weekday (client overrides have no "origin").
    allScheduleGroups() {
      return this.groupByDay(this.schedules);
    },

    // Checkbox list grouped by weekday, excluding the original itself.
    replacementGroups() {
      const groups = new Map();
      for (const s of this.schedules) {
        if (s.id === this.editing.referenceSchedulerId) continue;
        const key = s.dayName || 'Unscheduled';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(s);
      }
      const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'Unscheduled'];
      return [...groups.entries()]
        .sort((a, b) => dayOrder.indexOf(a[0]) - dayOrder.indexOf(b[0]))
        .map(([day, items]) => ({ day, items: items.sort((x, y) => (x.time || '').localeCompare(y.time || '')) }));
    }
  },
  async mounted() {
    await authModule.ready();
    await this.load();
  },
  methods: {
    scheduleName(id) {
      const s = this.scheduleById[id];
      return s ? s.name : '(inactive schedule)';
    },
    scheduleMeta(id) {
      const s = this.scheduleById[id];
      return s && s.dayName ? `${s.dayName}${s.time ? ' ' + s.time : ''}` : '';
    },
    genderOf(id) {
      const g = this.scheduleById[id]?.gender;
      return g === 0 ? 'male' : g === 1 ? 'female' : 'all';
    },
    genderClass(id) { return this.genderOf(id); },
    genderShort(id) {
      const g = this.genderOf(id);
      return g === 'male' ? 'M' : g === 'female' ? 'F' : 'All';
    },
    // One row per audience: if every target is open to everyone -> a single
    // "All clients" row; otherwise a Male row and a Female row showing where
    // each goes (a Male-only target is hidden from female clients, and vice versa).
    // Restriction of a target = the edge condition ({ id, gender }) saved here.
    // The schedule's own gender flag is NOT used by the caregiver app.
    effectiveGender(e) {
      if (e.gender === 0) return 'male';
      if (e.gender === 1) return 'female';
      return 'all';
    },
    effectiveGenderShort(e) {
      const g = this.effectiveGender(e);
      return g === 'male' ? 'M' : g === 'female' ? 'F' : 'All';
    },
    routesFor(m) {
      const targets = (m.makeupList || []).map(e => (typeof e === 'string' ? { id: e, gender: null } : e));
      const forMale = targets.filter(e => this.effectiveGender(e) !== 'female');
      const forFemale = targets.filter(e => this.effectiveGender(e) !== 'male');
      const same = forMale.length === forFemale.length && forMale.every((e, i) => e.id === forFemale[i].id);
      if (same) return [{ who: 'all', label: 'All clients', targets }];
      return [
        { who: 'male', label: 'Male clients', targets: forMale },
        { who: 'female', label: 'Female clients', targets: forFemale },
      ];
    },

    labelOf(s) {
      return `${s.name}${s.dayName ? ` (${s.dayName}${s.time ? ' ' + s.time : ''})` : ''}`;
    },

    scheduleLabel(id) {
      const s = this.scheduleById[id];
      return s ? this.labelOf(s) : '(inactive schedule)';
    },

    async load() {
      this.loading = true;
      try {
        const [{ data }, { data: rulesData }, { data: clientData }] = await Promise.all([
          axios.get('/api/makeup-schedulers'),
          axios.get('/api/makeup-schedulers/settings'),
          axios.get('/api/makeup-schedulers/client-mappings')
        ]);
        this.mappings = data.mappings || [];
        this.schedules = data.schedules || [];
        if (rulesData?.settings) this.rules = { ...this.rules, ...rulesData.settings };
        this.clientMappings = clientData?.mappings || {};
        // Client list is only needed for the override picker — load lazily, best-effort.
        if (!this.clients.length) {
          axios.get('/api/makeup-requests/staff-meta')
            .then(({ data: meta }) => { this.clients = meta.clients || []; })
            .catch(() => {});
        }
      } catch (e) {
        this.$bvToast.toast('Failed to load swap mappings', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.loading = false;
      }
    },

    async saveRules() {
      this.savingRules = true;
      try {
        await axios.put('/api/makeup-schedulers/settings', this.rules);
        this.$bvToast.toast('Swap rules saved — the caregiver app picks them up within a minute', {
          title: 'Saved', variant: 'success', solid: true
        });
      } catch (error) {
        this.$bvToast.toast(error?.response?.data?.error || 'Failed to save swap rules', {
          title: 'Error', variant: 'danger', solid: true
        });
      } finally {
        this.savingRules = false;
      }
    },

    groupByDay(list) {
      const groups = new Map();
      for (const s of list) {
        const key = s.dayName || 'Unscheduled';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(s);
      }
      const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'Unscheduled'];
      return [...groups.entries()]
        .sort((a, b) => dayOrder.indexOf(a[0]) - dayOrder.indexOf(b[0]))
        .map(([day, items]) => ({ day, items: items.sort((x, y) => (x.time || '').localeCompare(y.time || '')) }));
    },

    clientLabel(id) {
      const c = this.clients.find(x => x.id.toLowerCase() === String(id).toLowerCase());
      return c ? (c.no || c.name) : id;
    },

    openClientEditor(entry) {
      this.clientSearch = '';
      this.clientEditing = entry
        ? { clientId: entry.clientId, name: entry.name || this.clientLabel(entry.clientId), schedulers: [...entry.schedulers], swapWindowDays: entry.swapWindowDays || '', existing: true }
        : { clientId: null, name: '', schedulers: [], swapWindowDays: '', existing: false };
      this.$bvModal.show('client-mapping-editor');
    },

    async saveClientMapping() {
      this.savingClient = true;
      try {
        const name = this.clientEditing.name || this.clientLabel(this.clientEditing.clientId);
        const raw = this.clientEditing.swapWindowDays;
        const { data } = await axios.put('/api/makeup-schedulers/client-mappings', {
          clientId: this.clientEditing.clientId,
          name,
          schedulers: this.clientEditing.schedulers,
          swapWindowDays: raw === '' || raw === null || raw === undefined ? null : Number(raw)
        });
        this.clientMappings = data.mappings || {};
        this.$bvToast.toast('Client override saved — the caregiver app picks it up within a minute', { title: 'Saved', variant: 'success', solid: true });
        this.$bvModal.hide('client-mapping-editor');
      } catch (error) {
        this.$bvToast.toast(error?.response?.data?.error || 'Failed to save the client override', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.savingClient = false;
      }
    },

    async removeClientMapping(entry) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Remove the override for ${entry.name || this.clientLabel(entry.clientId)}? They will go back to their schedule's mapping.`,
        { title: 'Remove Client Override', okVariant: 'danger', okTitle: 'Remove', cancelTitle: 'Cancel', centered: true, buttonSize: 'sm' }
      );
      if (!confirmed) return;
      try {
        const { data } = await axios.put('/api/makeup-schedulers/client-mappings', { clientId: entry.clientId, schedulers: [], swapWindowDays: null });
        this.clientMappings = data.mappings || {};
        this.$bvToast.toast('Client override removed', { title: 'Removed', variant: 'success', solid: true });
      } catch (error) {
        this.$bvToast.toast(error?.response?.data?.error || 'Failed to remove the override', { title: 'Error', variant: 'danger', solid: true });
      }
    },

    openEditor(mapping, presetOriginId = null) {
      const entries = (mapping?.makeupList || []).map(e => (typeof e === 'string' ? { id: e, gender: null } : e));
      const genders = {};
      entries.forEach(e => { if (e.gender === 0 || e.gender === 1) genders[e.id] = e.gender; });
      this.editing = mapping
        ? { id: mapping.id, referenceSchedulerId: mapping.referenceSchedulerId, selected: entries.map(e => e.id), genders }
        : { id: null, referenceSchedulerId: presetOriginId, selected: [], genders: {} };
      this.$bvModal.show('swap-mapping-editor');
    },

    setEdgeGender(id, value) {
      const genders = { ...this.editing.genders };
      if (value === 'any') delete genders[id]; else genders[id] = Number(value);
      this.editing.genders = genders;
    },

    async saveMapping() {
      this.saving = true;
      try {
        await axios.post('/api/makeup-schedulers', {
          id: this.editing.id || undefined,
          referenceSchedulerId: this.editing.referenceSchedulerId,
          makeupList: this.editing.selected.map(id => (this.editing.genders[id] != null ? { id, gender: this.editing.genders[id] } : id))
        });
        this.$bvToast.toast('Swap mapping saved', { title: 'Saved', variant: 'success', solid: true });
        this.$bvModal.hide('swap-mapping-editor');
        await this.load();
      } catch (error) {
        this.$bvToast.toast(error?.response?.data?.error || 'Failed to save the mapping', {
          title: 'Error', variant: 'danger', solid: true
        });
      } finally {
        this.saving = false;
      }
    },

    async deleteMapping(mapping) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Remove the swap mapping for ${this.scheduleLabel(mapping.referenceSchedulerId)}? Caregivers will no longer be able to swap out of this session.`,
        { title: 'Delete Mapping', okVariant: 'danger', okTitle: 'Delete', cancelTitle: 'Cancel', centered: true, buttonSize: 'sm' }
      );
      if (!confirmed) return;
      try {
        await axios.delete(`/api/makeup-schedulers/${mapping.id}`);
        this.$bvToast.toast('Mapping deleted', { title: 'Deleted', variant: 'success', solid: true });
        await this.load();
      } catch (error) {
        this.$bvToast.toast(error?.response?.data?.error || 'Failed to delete the mapping', {
          title: 'Error', variant: 'danger', solid: true
        });
      }
    }
  }
};
</script>

<style scoped>
.swap-mapping-page {
  padding: 1.5rem;
  text-align: left;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
}

.rules-card {
  border: 1px solid #ececf1;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.25rem;
  background: #fafafc;
}

.rules-title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 0.75rem;
}

.rules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.rule-item label {
  font-weight: 600;
  font-size: 0.85rem;
  margin-bottom: 0.2rem;
}

.mapping-legend { display: flex; flex-wrap: wrap; align-items: center; gap: 1rem; font-size: 0.8rem; color: #6b7280; margin: 0.25rem 0 0.75rem; }
.legend-item { display: inline-flex; align-items: center; gap: 0.35rem; }
.legend-note { margin-left: auto; font-style: italic; }
.mapping-table { width: 100%; border-collapse: separate; border-spacing: 0; background: #fff; border: 1px solid #ececf1; border-radius: 12px; overflow: hidden; margin-bottom: 1rem; }
.mapping-table th { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #9ca3af; background: #fafafc; padding: 0.6rem 0.9rem; border-bottom: 1px solid #ececf1; text-align: left; }
.mapping-table td { padding: 0.6rem 0.9rem; vertical-align: top; border-bottom: 1px solid #f3f1f7; }
.mapping-table tr.row-first td { border-top: 1px solid #ececf1; }
.col-origin { min-width: 240px; }
.col-who { white-space: nowrap; width: 140px; }
.col-actions { white-space: nowrap; width: 140px; }
.origin-meta { font-size: 0.78rem; color: #6b7280; margin: 0.1rem 0 0.25rem; }
.chip-meta { color: #7c6a8c; font-weight: 500; }
.gender-pill { display: inline-block; font-size: 0.68rem; font-weight: 800; border-radius: 999px; padding: 0.05rem 0.45rem; line-height: 1.2; }
.gender-pill.inline { margin-left: 0.35rem; }
.gender-pill.male { background: #dbeafe; color: #1d4ed8; }
.gender-pill.female { background: #fce7f3; color: #be185d; }
.gender-pill.all { background: #e5e7eb; color: #374151; }
.who-pill { display: inline-block; font-size: 0.78rem; font-weight: 700; border-radius: 999px; padding: 0.15rem 0.6rem; }
.who-pill.male { background: #dbeafe; color: #1d4ed8; }
.who-pill.female { background: #fce7f3; color: #be185d; }
.who-pill.all { background: #e5e7eb; color: #374151; }

.mapping-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  border: 1px solid #ececf1;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin-bottom: 0.75rem;
  background: #fff;
}

.mapping-main {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex: 1;
  min-width: 0;
}

.mapping-arrow {
  color: #50276b;
  flex-shrink: 0;
}

.mapping-origin {
  min-width: 220px;
}

.window-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.3rem;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #0e7c7b;
  background: #e6f4f4;
}

.origin-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #9ca3af;
  margin-bottom: 0.2rem;
}

.target-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.target-chip {
  background: #efe6f7;
  color: #50276b;
  border-radius: 999px;
  padding: 0.15rem 0.7rem;
  font-size: 0.8rem;
  font-weight: 600;
}

.target-chip.unmapped {
  background: #fff3cd;
  color: #856404;
  cursor: pointer;
}

.mapping-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.client-box {
  border: 1px solid #d9e6e4;
  background: #f6fbfa;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin-top: 1.25rem;
}

.unmapped-box {
  border: 1px dashed #e5c766;
  background: #fffdf4;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin-top: 1.25rem;
}

.form-section {
  margin-bottom: 1.1rem;
}

.section-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 0.4rem;
}

.target-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.2rem;
}
.who-select {
  max-width: 190px;
}

.day-group {
  margin-bottom: 0.6rem;
}

.day-group-title {
  font-weight: 700;
  font-size: 0.82rem;
  color: #50276b;
  margin-bottom: 0.2rem;
}
</style>
